import { CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SercomCheckbox from '../../components/SercomCheckbox';
import SercomDialog from '../../components/SercomDialog';
import snackbarHelper from '../../helpers/snackbar-helper';
import AdminService from '../../services/admin-service';
import SercomEnums from '../../support/sercom-enums';
import { SercomTypes } from '../../support/sercom-types';

type QuickBooksLinks = {
  companyId: string;
  itemsCount: number;
  taxesCount: number;
  clientsCount: number;
};

const ClearQuickBooksLinksPopup = (props: SercomTypes.SercomDialog) => {
  const { t } = useTranslation();
  const [links, setLinks] = useState<QuickBooksLinks>();
  const [clearItems, setClearItems] = useState(false);
  const [clearTaxes, setClearTaxes] = useState(false);
  const [clearClients, setClearClients] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const companyName = props.data?.companyName ?? '';

  useEffect(() => {
    async function loadLinks() {
      if (!props.id) {
        return;
      }
      const result = await AdminService.getQuickBooksLinks(props.id);
      if (result?.isSuccess && result.data?.data) {
        setLinks(result.data.data);
      } else if (result?.data?.errorCode) {
        snackbarHelper.showErrorFromCode(result.data.errorCode, t);
        props.onClose();
      } else {
        snackbarHelper.showErrorFromCode(SercomEnums.ErrorCode.ServerError, t);
        props.onClose();
      }
    }
    loadLinks();
  }, []);

  const onClear = async () => {
    if (!props.id || !links) {
      return;
    }
    setIsClearing(true);
    const result = await AdminService.clearQuickBooksLinks(props.id, clearItems, clearTaxes, clearClients);
    setIsClearing(false);
    if (result?.isSuccess && result.data?.data) {
      snackbarHelper.showSuccess(t('QuickBooksLinksClearedSuccessfully'));
      props.action();
      props.onClose();
    } else if (result?.data?.errorCode) {
      snackbarHelper.showErrorFromCode(result.data.errorCode, t);
    } else {
      snackbarHelper.showErrorFromCode(SercomEnums.ErrorCode.ServerError, t);
    }
  };

  return (
    <SercomDialog
      title={t('ClearQuickBooksLinks')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      buttonText={t('Clear')}
      actionDisabled={isClearing || !links || (!clearItems && !clearTaxes && !clearClients)}
      action={onClear}
    >
      {links ? (
        <Stack spacing={1.5} sx={{ minWidth: 360, mt: 1 }}>
          <Typography>{t('ClearQuickBooksLinksDescription', { company: companyName })}</Typography>
          <SercomCheckbox
            label={`${t('Items')} (${links.itemsCount})`}
            checked={clearItems}
            disabled={links.itemsCount == 0}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setClearItems(event.target.checked)}
          />
          <SercomCheckbox
            label={`${t('Taxes')} (${links.taxesCount})`}
            checked={clearTaxes}
            disabled={links.taxesCount == 0}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setClearTaxes(event.target.checked)}
          />
          <SercomCheckbox
            label={`${t('Clients')} (${links.clientsCount})`}
            checked={clearClients}
            disabled={links.clientsCount == 0}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setClearClients(event.target.checked)}
          />
        </Stack>
      ) : (
        <Stack alignItems="center" sx={{ minWidth: 360, py: 3 }}>
          <CircularProgress size={28} />
        </Stack>
      )}
    </SercomDialog>
  );
};

export default ClearQuickBooksLinksPopup;
